import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadMediaResponse } from '@/client/types/cms';
import { mediaKeys } from './media.query';

type DeleteMediaResponse = {
  status: number;
  message?: string;
};

// FormData upload bypasses the JSON body path; we use fetch directly so the
// browser sets the proper multipart boundary. Session cookies are forwarded
// automatically via `credentials: 'include'`.
const uploadMedia = async (file: File): Promise<UploadMediaResponse> => {
  const form = new FormData();
  form.append('files[]', file, file.name);

  const res = await fetch('/api/v2/asset/upload', {
    method: 'POST',
    body: form,
    credentials: 'include',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = 'Upload failed';
    try { message = JSON.parse(text)?.message ?? message; } catch { /* ignore */ }
    throw new Error(message);
  }

  return res.json();
};

const useDeleteMediaFn = () => {
  const request = useClientStore(state => state.request);
  return withErrorHandling(
    async (asid: string): Promise<DeleteMediaResponse> =>
      request<DeleteMediaResponse>(`/api/v2/asset/${asid}`, 'DELETE'),
    'An unexpected error occurred while deleting the asset',
  );
};

export const useUploadMediaMutation = () => {
  const queryClient = useQueryClient();
  return useMutation<UploadMediaResponse, Error, File>({
    mutationFn: uploadMedia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
    },
  });
};

export const useDeleteMediaMutation = () => {
  const queryClient = useQueryClient();
  const mutationFn = useDeleteMediaFn();
  return useMutation<DeleteMediaResponse, Error, string>({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
    },
  });
};
